import { Link } from 'react-router-dom';
import { SERVICE_PACKAGES } from '../../shared/packages';

const LIFECYCLE = [
  ['Intake', 'Tell us your goal — we score fit and prepare before we ever meet.'],
  ['Discovery', 'Structured workshop: context, stakeholders, systems, constraints.'],
  ['Assessment', '10-dimension AI maturity scorecard with evidence and gaps.'],
  ['Portfolio', 'Use cases scored on value, feasibility, risk, and time-to-value.'],
  ['NIST Mapping', 'Govern · Map · Measure · Manage — controls fit to your context.'],
  ['Roadmap', '30/60/90 plan, pilots, business case, executive readout.'],
  ['Delivery', 'Implementation sprints with guardrails and value tracking.'],
] as const;

export function Landing() {
  return (
    <div className="min-h-full">
      <header className="border-b border-slate-800">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4">
          <div className="flex items-center gap-2 text-lg font-bold">
            <span className="bg-gradient-to-r from-brand-400 to-cyan-400 bg-clip-text text-transparent">VibeProSoft</span>
            <span className="text-slate-200">AI Consulting</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/login" className="text-sm text-slate-400 hover:text-slate-200">Consultant sign in</Link>
            <Link to="/start" className="btn-primary">Start your inquiry</Link>
          </div>
        </div>
      </header>

      <section className="mx-auto max-w-6xl px-4 pb-16 pt-20 text-center">
        <p className="mb-4 inline-block rounded-full border border-brand-700/50 bg-brand-950/30 px-4 py-1 text-xs font-semibold uppercase tracking-widest text-brand-300">
          NIST AI RMF–aligned · Outcome-priced packages
        </p>
        <h1 className="mx-auto max-w-3xl text-4xl font-extrabold leading-tight text-slate-50 sm:text-5xl">
          AI consulting that ships{' '}
          <span className="bg-gradient-to-r from-brand-400 to-cyan-400 bg-clip-text text-transparent">outcomes</span>,
          not slideware.
        </h1>
        <p className="mx-auto mt-5 max-w-2xl text-lg text-slate-400">
          From "where do we even start?" to governed agents in production. We assess your AI
          maturity, score your opportunities, map the risks against NIST&nbsp;AI&nbsp;RMF, and
          leave you with a funded roadmap — in weeks, not quarters.
        </p>
        <div className="mt-8 flex justify-center gap-4">
          <Link to="/start" className="btn-primary !px-6 !py-3 !text-base">Start your inquiry →</Link>
          <a href="#packages" className="btn-secondary !px-6 !py-3 !text-base">See packages</a>
        </div>
        <p className="mt-4 text-xs text-slate-600">
          Every inquiry gets a same-day AI-prepared pre-consulting brief. No spam, ever.
        </p>
      </section>

      <section className="border-y border-slate-800 bg-slate-900/40 py-12">
        <div className="mx-auto max-w-6xl px-4">
          <h2 className="mb-8 text-center text-sm font-semibold uppercase tracking-widest text-slate-500">
            How an engagement runs
          </h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {LIFECYCLE.map(([title, blurb], i) => (
              <div key={title} className="card !p-4">
                <div className="mb-1 flex items-center gap-2">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-brand-500/20 text-xs font-bold text-brand-300">{i + 1}</span>
                  <h3 className="font-semibold text-slate-100">{title}</h3>
                </div>
                <p className="text-xs leading-relaxed text-slate-400">{blurb}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="packages" className="mx-auto max-w-6xl px-4 py-16">
        <h2 className="text-center text-2xl font-bold text-slate-50">Five ways to engage</h2>
        <p className="mx-auto mt-2 max-w-xl text-center text-sm text-slate-400">
          Fixed-scope packages with named deliverables. Not sure which fits? The intake form will
          recommend one — and we'll confirm it on the discovery call.
        </p>
        <div className="mt-8 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {SERVICE_PACKAGES.map((p) => (
            <div key={p.id} className="card flex flex-col">
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-brand-400">{p.duration}</div>
              <h3 className="text-lg font-bold text-slate-50">{p.name}</h3>
              <p className="mt-1 text-sm text-slate-400">{p.tagline}</p>
              <ul className="mt-3 space-y-1 text-xs text-slate-300">
                {p.outputs.map((o) => (
                  <li key={o} className="flex gap-2"><span className="text-brand-400">✓</span>{o}</li>
                ))}
              </ul>
              <Link to={`/start?package=${p.id}`} className="btn-secondary mt-4 self-start !px-3 !py-1.5 text-xs">
                Inquire about this →
              </Link>
            </div>
          ))}
          <div className="card flex flex-col justify-center border-dashed">
            <h3 className="text-lg font-bold text-slate-200">Something custom?</h3>
            <p className="mt-1 text-sm text-slate-400">
              Describe your goal in the intake form — engagements can blend packages or run as a
              custom SOW after discovery.
            </p>
            <Link to="/start" className="btn-primary mt-4 self-start !px-4 !py-2 text-sm">Start your inquiry</Link>
          </div>
        </div>
      </section>

      <section className="border-t border-slate-800 bg-slate-900/40 py-12">
        <div className="mx-auto grid max-w-6xl gap-8 px-4 md:grid-cols-3">
          <div>
            <h3 className="font-bold text-slate-100">Governance is built in</h3>
            <p className="mt-1 text-sm text-slate-400">
              Every use case maps to the NIST AI Risk Management Framework — Govern, Map, Measure,
              Manage — with evidence checklists and documented go/no-go decisions for high-risk work.
            </p>
          </div>
          <div>
            <h3 className="font-bold text-slate-100">Multi-model, vendor-neutral</h3>
            <p className="mt-1 text-sm text-slate-400">
              Claude, Gemini, ChatGPT, open-weights — recommendations are based on your data
              classification, workload, and budget. Provenance is recorded on every AI-assisted artifact.
            </p>
          </div>
          <div>
            <h3 className="font-bold text-slate-100">Value you can defend</h3>
            <p className="mt-1 text-sm text-slate-400">
              Deterministic, explainable scoring — fit, urgency, and use-case priority all show their
              math. Baselines before pilots; scale/kill decisions with evidence.
            </p>
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-800 py-8 text-center text-xs text-slate-600">
        © {new Date().getFullYear()} VibeProSoft · AI Consulting Navigator ·{' '}
        <a href="https://vibeprosoft.com" className="hover:text-slate-400">vibeprosoft.com</a>
      </footer>
    </div>
  );
}
